import React from 'react';
import { api, formatINR } from '../../../lib/store';
import { formatSizeInput } from '../../../lib/utils';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight, Image as ImageIcon, X, Wrench, Upload, Package, Zap, Download } from 'lucide-react';
import { logger } from '../../../lib/logger';
import {
  CATEGORIES,
  RATE_TYPES,
  THICKNESS_BULK_CATEGORIES,
  WEIGHT_BULK_CATEGORIES,
  PLYWOOD_THICKNESSES,
  BLOCKBOARD_THICKNESSES,
  MDF_THICKNESSES,
  DOORS_THICKNESSES,
  WPCPVC_THICKNESSES,
  ACP_THICKNESSES,
  ADHESIVE_WEIGHTS,
  PLYWOOD_SIZES,
  DOOR_SIZES,
  DOOR_SIZES_WITH_SQFT,
  emptyProduct,
} from '../constants';

// Extracted from ProductManagement.js (R2). Receives parent state/handlers as props.
export function BulkUploadDialog(props) {
  const {
    bulkUploadRef,
    bulkUploading,
    handleBulkUpload,
    handleDownloadTemplate,
    products,
    setShowBulkUploadDialog,
    showBulkUploadDialog,
  } = props;
  return (
    <>
      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkUploadDialog} onOpenChange={setShowBulkUploadDialog}>
        <DialogContent className="max-w-lg" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Products</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-heda-mute">
              Download a template for your category, fill it with product data, then upload.
            </p>
            
            {/* Template Downloads */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Download Template</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('plywood')}
                  className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold text-xs justify-start"
                >
                  📋 Plywood
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('blockboard')}
                  className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold text-xs justify-start"
                >
                  📋 Blockboard
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('laminates')}
                  className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold text-xs justify-start"
                >
                  📋 Laminates
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('veneers')}
                  className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold text-xs justify-start"
                >
                  📋 Veneers
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('hardware')}
                  className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold text-xs justify-start"
                >
                  📋 Hardware (Simple)
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('hardware-systems')}
                  className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold text-xs justify-start"
                >
                  📋 Hardware Systems
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('screws-patti')}
                  className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold text-xs justify-start"
                >
                  📋 Screws/Patti
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('adhesives')}
                  className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold text-xs justify-start"
                >
                  📋 Adhesives
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('doors')}
                  className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold text-xs justify-start"
                >
                  📋 Doors
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('')}
                  className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold text-xs justify-start"
                >
                  📋 All Categories
                </Button>
              </div>
            </div>
            
            <Separator />
            
            {/* Upload Section */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Upload Filled Template</Label>
              <div className="border-2 border-dashed border-heda-line rounded-xl p-6 text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleBulkUpload}
                  ref={bulkUploadRef}
                  className="hidden"
                  id="bulk-upload-input"
                />
                <label htmlFor="bulk-upload-input" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-heda-mute mb-2" />
                  <p className="text-sm text-heda-mute">
                    {bulkUploading ? 'Uploading...' : 'Click to upload Excel file'}
                  </p>
                  <p className="text-xs text-heda-mute mt-1">.xlsx or .xls files only</p>
                </label>
              </div>
            </div>
            
            <p className="text-xs text-heda-mute">
              <strong>Note:</strong> Existing products with matching brand + code will be updated. 
              New products will be created.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
